import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Icon } from "@/components/Icon";
import api from "@/lib/api";
import PlayingCard, { CardHand } from "@/components/PlayingCard";
import useBlackjackDealerDisplayTable from "@/components/live-blackjack/useBlackjackDealerDisplayTable";
import { decisionLabel } from "@/components/live-blackjack/tableConfig";
import pnrbetIcon from "@/assets/pnrbet-icon.png";

function awaitingHeadline(table) {
  const aw = table?.awaiting;
  if (!aw) return null;
  const seat = table.seats?.[aw.seat];
  const split = (seat?.hands?.length || 0) > 1 && aw.reason === "initial";
  if (aw.reason === "hit") return `SEAT #${aw.seat + 1} — HIT · DEAL CARD`;
  if (aw.reason === "double") return `SEAT #${aw.seat + 1} — DOUBLE · DEAL ONE CARD`;
  if (split) return `SEAT #${aw.seat + 1} — SPLIT · DEAL CARD`;
  return `SEAT #${aw.seat + 1} — DEAL CARD`;
}

function seatDecision(table, seat) {
  if (!seat?.occupied) return "EMPTY";
  const active = table.status === "acting" && table.active_seat === seat.seat;
  const handIndex = active ? (table.active_hand || 0) : 0;
  const hand = seat.hands?.[handIndex];
  const reason = table.awaiting?.seat === seat.seat ? table.awaiting.reason : null;
  if (reason === "initial" && (seat.hands?.length || 0) > 1) return "SPLIT";
  if (table.status === "betting" && hand?.bet > 0) return "BET LOCKED";
  if (table.status === "dealing") return hand?.bet > 0 ? "DEALING" : "NO BET";
  if (table.status === "dealer") return "DONE";
  if (table.status === "settled") return String(hand?.result || "DONE").toUpperCase();
  return decisionLabel(hand, active && !table.awaiting, reason) || (hand?.bet > 0 ? "WAITING" : "NO BET");
}

/* Presentation-only helpers (no realtime/state logic). */
const PHASES = [
  { key: "betting", label: "Betting" },
  { key: "dealing", label: "Dealing" },
  { key: "acting", label: "Player turns" },
  { key: "dealer", label: "Dealer" },
  { key: "settle", label: "Settle" },
];

function phaseIndex(status) {
  if (status === "betting") return 0;
  if (status === "dealing") return 1;
  if (status === "acting") return 2;
  if (status === "dealer") return 3;
  if (status === "settled") return 4;
  return -1;
}

// Green group vs red group vs neutral for a seat status pill.
function pillTone(label) {
  if (["HIT", "DOUBLE", "DECIDING", "WIN", "BLACKJACK", "EVEN MONEY"].includes(label)) return "green";
  if (["BUST", "LOSS", "SURRENDER"].includes(label)) return "red";
  return "neutral";
}

export default function BlackjackDealerDisplay({ adminNav = false }) {
  const { tableId = "main" } = useParams();
  const navigate = useNavigate();
  const [clock, setClock] = useState(Date.now());

  const [displayToken, setDisplayToken] = useState(() => {
    // Admin route mints its own fresh token below — start empty so we never reuse
    // a stale sessionStorage token from a public link.
    if (adminNav) return "";
    const storageKey = `pnrbet-dealer-display:${tableId}`;
    const hashParams = new URLSearchParams((window.location.hash || "").replace(/^#/, ""));
    const incoming = hashParams.get("display_token") || "";
    if (incoming) {
      try { sessionStorage.setItem(storageKey, incoming); } catch {}
      try {
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
      } catch {}
      return incoming;
    }
    try { return sessionStorage.getItem(storageKey) || ""; } catch { return ""; }
  });

  // Admin-protected dealer TV: mint a scoped display token with the admin session so
  // the TV shows the correct player view (hole card hidden) — no guessable public link.
  useEffect(() => {
    if (!adminNav) return;
    let alive = true;
    api
      .post(`/admin/bj/dealer-display-token?table=${tableId}`)
      .then(({ data }) => { if (alive && data?.display_token) setDisplayToken(data.display_token); })
      .catch(() => {});
    return () => { alive = false; };
  }, [adminNav, tableId]);

  const { state: table, transport, error, lastUpdateAt } = useBlackjackDealerDisplayTable(tableId, displayToken);

  useEffect(() => {
    const id = setInterval(() => setClock(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let wakeLock;
    let cancelled = false;
    const request = async () => {
      try {
        if (!document.hidden && navigator.wakeLock?.request) {
          wakeLock = await navigator.wakeLock.request("screen");
          if (cancelled) await wakeLock.release();
        }
      } catch {}
    };
    const onVisibility = () => {
      if (!document.hidden && (!wakeLock || wakeLock.released)) request();
    };
    request();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      try { wakeLock?.release(); } catch {}
    };
  }, []);

  const stale = Boolean(lastUpdateAt && clock - lastUpdateAt > 10000);

  const dealerRevealed = table?.status === "dealer" || table?.status === "settled";
  const dealerCardsForDisplay = dealerRevealed
    ? (table?.dealer || [])
    : (table?.dealer?.length
        ? [table.dealer[0], { rank: "?", suit: "?" }]
        : []);

  const activeSeat = table?.status === "acting" && table?.active_seat != null ? table.seats?.[table.active_seat] : null;
  const headline = !table ? "CONNECTING" :
    table.status === "acting" && table.awaiting ? awaitingHeadline(table) :
    table.status === "acting" && activeSeat ? `SEAT #${table.active_seat + 1} — WAITING FOR DECISION` :
    table.status === "dealer" ? "DEALER TURN" :
    table.status === "betting" ? "BETTING OPEN" :
    table.status === "dealing" ? "OPENING DEAL" :
    table.status === "settled" ? "ROUND COMPLETE" :
    table.status === "off" ? "TABLE OFF" : "WAITING";

  if (error && !table) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-black p-6 text-white" data-testid="dealer-tv-error">
        <div className="max-w-lg rounded-3xl border border-[#EF4444]/30 bg-[#EF4444]/10 p-8 text-center">
          <p className="text-xs font-black uppercase tracking-[0.3em] text-[#EF4444]">Dealer display unavailable</p>
          <h1 className="mt-3 text-3xl font-black">Open a fresh dealer TV link from the operator console.</h1>
          <p className="mt-3 text-sm text-white/45">{error}</p>
        </div>
      </div>
    );
  }

  // --- Presentation-only derived values ---------------------------------
  const activePhase = phaseIndex(table?.status);
  // The active seat's hand + context line for the hero (acting phase only).
  const heroSeatNumber = activeSeat ? (table.active_seat + 1) : null;
  const heroHand = activeSeat ? activeSeat.hands?.[table.active_hand || 0] : null;
  const heroLabel = activeSeat ? seatDecision(table, activeSeat) : null;
  const dealerUp = table?.seats && table?.dealer?.length ? table.dealer[0] : null;
  // Split "SEAT #n — ACTION" into the green block + big action text.
  const headlineAction = typeof headline === "string" && headline.includes("—")
    ? headline.split("—").slice(1).join("—").trim()
    : headline;

  // Turn order + up-next (acting phase) — the seated players with a bet act in seat order.
  const actingSeats = (table?.seats || []).filter((s) => s.occupied && s.hands?.some((h) => h.bet > 0));
  const turnIdx = activeSeat ? actingSeats.findIndex((s) => s.seat === table.active_seat) : -1;
  const turnPos = turnIdx >= 0 ? turnIdx + 1 : null;
  const turnTotal = actingSeats.length;
  const upNext = turnIdx >= 0 && turnIdx + 1 < actingSeats.length ? actingSeats[turnIdx + 1] : null;
  const seatCount = table?.seats?.length || 7;
  const seatsIn = (table?.seats || []).filter((s) => s.occupied).length;
  const secs = table?.seconds_left != null ? Math.max(0, Math.ceil(table.seconds_left)) : null;
  const clockStr = secs != null ? `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}` : null;

  return (
    <div
      className="relative min-h-screen bg-[#0A0A0A] p-4 text-white sm:p-8"
      data-testid="dealer-tv"
      style={{
        backgroundImage:
          "radial-gradient(120% 80% at 50% 0%, rgba(255,255,255,0.045) 0%, rgba(255,255,255,0) 55%)",
      }}
    >
      <div className="mx-auto flex min-h-[calc(100vh-2rem)] max-w-[1800px] flex-col gap-5">
        {/* 0) ADMIN NAV (admin-protected route only) --------------------- */}
        {adminNav && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#1F1F23] bg-[#0C0C0E] px-4 py-2.5">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => navigate("/admin")}
                data-testid="dealer-tv-back-admin"
                className="inline-flex items-center gap-1.5 rounded-[10px] border border-[#1F1F23] bg-[#141417] px-3 py-1.5 font-body text-xs font-bold text-[#a1a1aa] transition-colors hover:border-[#2a2a30] hover:text-white"
              >
                <Icon name="arrow-left" size={16} /> Admin
              </button>
              <button
                type="button"
                onClick={() => navigate(`/admin/live-blackjack/${tableId}/operator`)}
                className="inline-flex items-center gap-1.5 rounded-[10px] border border-[#1F1F23] bg-[#141417] px-3 py-1.5 font-body text-xs font-bold text-[#a1a1aa] transition-colors hover:border-[#2a2a30] hover:text-white"
              >
                <Icon name="broadcast" size={16} /> Operator console
              </button>
            </div>
            <span className="font-body text-[11px] font-black uppercase tracking-[0.25em] text-[#71717a]">
              Dealer TV · {table?.name || tableId}
            </span>
          </div>
        )}

        {/* 1) TOP BAR ---------------------------------------------------- */}
        <div className="flex items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-[#1F1F23] bg-[#0C0C0E]">
              <img src={pnrbetIcon} alt="PRYMEBET" className="h-8 w-8 object-contain" />
            </div>
            <div>
              <p className="font-body text-xs font-bold uppercase tracking-[0.35em] text-[#00D632]">
                PRYMEBET LIVE · DEALER DISPLAY
              </p>
              <h1 className="font-display mt-1 text-3xl font-extrabold tracking-tight sm:text-5xl">
                {table?.name || tableId}
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-6">
            {/* Round-phase stepper */}
            <div className="hidden items-center lg:flex">
              {PHASES.map((p, i) => {
                const on = i === activePhase;
                return (
                  <React.Fragment key={p.key}>
                    {i > 0 && (
                      <span className={`mx-1 h-px w-8 ${i <= activePhase ? "bg-[#00D632]/50" : "bg-[#1F1F23]"}`} />
                    )}
                    {on ? (
                      <span className="flex items-center gap-2 rounded-full bg-[#00D632] px-3 py-1.5">
                        <span className="h-2 w-2 rounded-full bg-black" />
                        <span className="font-body text-xs font-bold uppercase tracking-wider text-black">
                          {p.label}
                        </span>
                      </span>
                    ) : (
                      <span className="flex items-center gap-2 px-2 py-1.5">
                        <span className={`h-2 w-2 rounded-full ${i < activePhase ? "bg-[#00D632]/60" : "bg-[#2a2a30]"}`} />
                        <span className="font-body text-xs font-semibold uppercase tracking-wider text-white/40">
                          {p.label}
                        </span>
                      </span>
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {/* Connection pill + round + fullscreen */}
            <div className="flex items-center gap-3">
              <div
                className={`flex items-center gap-2 rounded-full border px-3 py-1.5 font-body text-xs font-bold uppercase tracking-wider ${
                  transport === "realtime" && !stale
                    ? "border-[#00D632]/40 bg-[#00D632]/10 text-[#00D632]"
                    : stale
                      ? "border-[#EF4444]/40 bg-[#EF4444]/10 text-[#EF4444]"
                      : "border-[#1F1F23] bg-[#111114] text-white/45"
                }`}
              >
                <Icon
                  name="wifi"
                  size={16}
                  className={
                    transport === "realtime" && !stale
                      ? "text-[#00D632]"
                      : stale
                        ? "text-[#EF4444]"
                        : "text-white/45"
                  }
                />
                {stale ? "STALE" : transport === "realtime" ? "REALTIME" : "FALLBACK"}
              </div>

              <div className="text-right">
                <p className="font-body text-[10px] font-bold uppercase tracking-[0.3em] text-white/35">Round</p>
                <p className="font-display text-2xl font-extrabold leading-none text-white/80">
                  {table?.round_number ?? "—"}
                </p>
              </div>

              <button
                type="button"
                onClick={() => document.documentElement.requestFullscreen?.().catch?.(() => {})}
                className="rounded-full border border-[#1F1F23] bg-[#111114] p-2.5 text-white/45 hover:text-white"
                aria-label="Fullscreen dealer display"
              >
                <Icon name="panel-collapse" size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* 2) HERO — CURRENT INSTRUCTION -------------------------------- */}
        {stale ? (
          <div className="flex items-center justify-center rounded-3xl border-2 border-[#EF4444]/60 bg-[#EF4444]/15 px-6 py-10 text-center">
            <div>
              <p className="font-body text-sm font-bold uppercase tracking-[0.3em] text-[#EF4444]">Connection warning</p>
              <p className="font-display mt-2 text-4xl font-extrabold text-[#EF4444] sm:text-6xl">
                LIVE STATE STALE — DEALER SHOULD PAUSE
              </p>
              <p className="mt-3 font-body text-base text-[#EF4444]/80">
                Hold play until the live connection recovers.
              </p>
            </div>
          </div>
        ) : (
          <div
            className="rounded-3xl border border-[#1F1F23] bg-[#0C0C0E] p-6 sm:p-8"
            style={{
              backgroundImage:
                "linear-gradient(120deg, rgba(0,214,50,0.10) 0%, rgba(0,214,50,0.02) 40%, rgba(12,12,14,0) 70%)",
            }}
          >
            {heroSeatNumber ? (
              <div className="flex items-center gap-6 sm:gap-8">
                {/* Green SEAT block */}
                <div className="flex h-28 w-28 shrink-0 flex-col items-center justify-center rounded-2xl bg-[#00D632] sm:h-36 sm:w-36">
                  <span className="font-body text-xs font-bold uppercase tracking-[0.25em] text-black/70">Seat</span>
                  <span className="font-display text-6xl font-extrabold leading-none text-black sm:text-7xl">
                    {heroSeatNumber}
                  </span>
                </div>
                {/* Instruction + context */}
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-2 font-body text-sm font-bold uppercase tracking-[0.25em] text-[#00D632]">
                    <span className="text-[#00D632]">●</span> Current instruction
                  </p>
                  <p className="font-display mt-2 truncate text-5xl font-extrabold leading-tight sm:text-7xl">
                    {headlineAction}
                  </p>
                  <p className="mt-3 font-body text-xl font-medium sm:text-2xl">
                    <span className="text-white">{activeSeat.username}</span>
                    {heroLabel && (
                      <>
                        <span className="mx-2 text-white/25">·</span>
                        <span className="font-bold text-[#00D632]">{heroLabel}</span>
                      </>
                    )}
                    {heroHand?.value != null && (
                      <>
                        <span className="mx-2 text-white/25">·</span>
                        <span className="text-white/50">on {heroHand.value}</span>
                      </>
                    )}
                    {dealerUp && dealerUp.rank !== "?" && (
                      <>
                        <span className="mx-2 text-white/25">·</span>
                        <span className="text-white/50">vs dealer {dealerUp.rank}</span>
                      </>
                    )}
                  </p>
                </div>
                {/* Right: TURN ORDER + UP NEXT (matches 09 mockup) */}
                <div className="hidden shrink-0 flex-col items-end gap-4 lg:flex">
                  {turnPos && (
                    <>
                      <div className="text-right">
                        <div className="flex items-center justify-end gap-3">
                          <span className="font-body text-xs font-bold uppercase tracking-[0.2em] text-white/40">Turn order</span>
                          <span className="font-display text-sm font-bold text-white/60">{turnPos} of {turnTotal}</span>
                        </div>
                        <div className="mt-2 flex items-center gap-2.5">
                          {Array.from({ length: seatCount }).map((_, i) => {
                            const pos = actingSeats.findIndex((s) => s.seat === i);
                            const isActive = table.active_seat === i;
                            const done = pos >= 0 && pos < turnIdx;
                            return (
                              <span key={i} className="font-display text-sm font-extrabold tabular-nums"
                                style={{ color: isActive ? "#00D632" : done ? "rgba(255,255,255,0.55)" : "rgba(255,255,255,0.2)" }}>{i + 1}</span>
                            );
                          })}
                        </div>
                      </div>
                      {upNext && (
                        <div className="rounded-2xl border border-[#1F1F23] bg-[#111114] px-5 py-3 text-right">
                          <p className="font-body text-[10px] font-bold uppercase tracking-[0.2em] text-white/35">Up next</p>
                          <p className="font-display mt-1 text-xl font-extrabold">Seat {upNext.seat + 1} · {upNext.username}</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="py-4 text-center sm:py-8">
                <p className="flex items-center justify-center gap-2 font-body text-sm font-bold uppercase tracking-[0.25em] text-[#00D632]">
                  <span className="text-[#00D632]">●</span> Current instruction
                </p>
                <p className="font-display mt-3 text-5xl font-extrabold leading-tight sm:text-8xl">
                  {headline}
                </p>
              </div>
            )}
          </div>
        )}

        {/* 3) SEATS ----------------------------------------------------- */}
        <div className={`grid flex-1 grid-cols-7 gap-3 ${stale ? "opacity-40" : ""}`}>
          {table?.seats?.map((seat) => {
            const active = table.status === "acting" && table.active_seat === seat.seat;
            const hand = seat.occupied ? seat.hands?.[active ? (table.active_hand || 0) : 0] : null;
            const label = seatDecision(table, seat);
            const bet = hand?.bet;

            if (!seat.occupied) {
              return (
                <div
                  key={seat.seat}
                  className="flex flex-col items-center justify-center gap-2 rounded-3xl border-2 border-dashed border-[#1F1F23] bg-transparent p-4 text-center"
                >
                  <p className="font-body text-xs font-bold uppercase tracking-[0.2em] text-white/30">
                    Seat {seat.seat + 1}
                  </p>
                  <p className="font-body text-sm font-semibold uppercase tracking-widest text-white/20">Open</p>
                </div>
              );
            }

            const tone = pillTone(label);
            const totalColor = tone === "red" ? "#EF4444" : tone === "green" ? "#00D632" : "#FFFFFF";
            return (
              <div
                key={seat.seat}
                className="relative flex flex-col overflow-hidden rounded-[24px] border bg-[#111114]"
                style={active
                  ? { borderColor: "#00D632", boxShadow: "0 0 0 1px #00D632, 0 0 40px rgba(0,214,50,0.35)" }
                  : { borderColor: "#1F1F23", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.043), 0 14px 36px rgba(0,0,0,0.35)" }}
              >
                {active && (
                  <span className="absolute -top-3 left-1/2 z-10 -translate-x-1/2 rounded-full bg-[#00D632] px-3 py-1 font-body text-[11px] font-bold uppercase tracking-wider text-black">
                    ● Now
                  </span>
                )}
                {/* Header — seat label + bet chip, then username */}
                <div className="border-b border-[#1F1F23] px-5 py-4">
                  <div className="flex h-[30px] items-center justify-between gap-2">
                    <span className="font-body text-[14px] font-extrabold uppercase tracking-[0.24em] text-[#71717a]">Seat {seat.seat + 1}</span>
                    {bet > 0 && (
                      <span className="flex items-center gap-2 rounded-full bg-[#18181C] px-2.5 py-1" style={{ boxShadow: "inset 0 0 0 1px #26262B" }}>
                        <span className="h-4 w-4 rounded-full border-[3px] border-dotted border-[#71717a] bg-[#26262B]" />
                        <span className="font-display text-[17px] font-bold tabular-nums text-[#E4E4E7]">${bet}</span>
                      </span>
                    )}
                  </div>
                  <p className="mt-1.5 truncate font-body text-[26px] font-bold leading-tight text-white">
                    {seat.username}
                    {seat.mode === "dealer" && <span className="ml-2 align-middle rounded bg-[#00D632]/20 px-1.5 py-0.5 text-[12px] font-black uppercase tracking-wide text-[#00D632]">Auto</span>}
                  </p>
                </div>

                {/* Body — fanned cards, big total, status */}
                <div className="flex flex-1 flex-col items-center justify-between gap-3 px-4 pb-4 pt-5">
                  <div className="flex min-h-[92px] items-center justify-center">
                    {hand?.cards?.length ? (
                      <CardHand cards={hand.cards} size="md" />
                    ) : (
                      <span className="font-display text-2xl font-black text-white/15">—</span>
                    )}
                  </div>
                  <div className="text-center">
                    <p className="font-body text-[10px] font-bold uppercase tracking-[0.22em] text-white/30">Total</p>
                    <p className="font-display text-[44px] font-extrabold leading-none tabular-nums" style={{ color: totalColor }}>
                      {hand?.cards?.length ? hand.value : "—"}
                    </p>
                  </div>
                  <div
                    className={`w-full rounded-xl px-2 py-2.5 text-center font-body text-sm font-bold uppercase tracking-wider ${
                      tone === "green" ? "bg-[#00D632] text-black" : tone === "red" ? "bg-[#EF4444] text-white" : "bg-[#1F1F23] text-white/80"
                    }`}
                  >
                    ● {label}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* 4) DEALER STRIP --------------------------------------------- */}
        <div
          className={`flex items-center justify-between gap-6 rounded-3xl border border-[#1F1F23] bg-[#0C0C0E] px-6 py-4 ${stale ? "opacity-40" : ""}`}
        >
          <div className="shrink-0">
            <p className="font-body text-sm font-bold uppercase tracking-[0.25em] text-white/45">Dealer</p>
            <p className="mt-0.5 font-body text-xs text-white/30">Stands on all 17s</p>
          </div>

          <div className="flex flex-1 items-center justify-center">
            {dealerCardsForDisplay.length ? (
              <CardHand cards={dealerCardsForDisplay} size="lg" overlap={46} />
            ) : (
              <span className="font-display text-3xl font-black text-white/15">—</span>
            )}
          </div>

          <div className="shrink-0 text-right">
            {dealerRevealed ? (
              <div className="rounded-2xl border border-[#1F1F23] bg-[#141417] px-5 py-2">
                <p className="font-body text-[10px] font-bold uppercase tracking-[0.2em] text-white/35">Total</p>
                <p className="font-display text-4xl font-extrabold leading-none">{table?.dealer_value ?? 0}</p>
              </div>
            ) : dealerUp && dealerUp.rank !== "?" ? (
              <div>
                <p className="font-display text-[40px] font-extrabold leading-none">
                  {dealerUp.rank}
                  <span className="ml-2 font-body text-xs font-bold uppercase tracking-[0.2em] text-white/35">Showing</span>
                </p>
                <p className="mt-1.5 font-body text-xs text-white/30">
                  <span className="font-bold uppercase tracking-[0.2em] text-white/40">Hole card</span> · Reveal after Seat 7 acts
                </p>
              </div>
            ) : (
              <div className="w-[92px]" aria-hidden="true" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
